import {
  FIELD_AVAILABILITY_KEY,
  FIELD_CONVERTED_AVAILABILITY_KEY,
  FIELD_ID_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { Alert } from "PFComponents/alert";
import { AVAILABILITY_MODES, AvailabilityInput, DURATION_MODES } from "PFComponents/availability";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import SidePanel from "PFComponents/side_panel/side_panel";
import { camelizeApiKeys } from "PFCore/api/utils";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useAvailabilityRequirementsValidation } from "PFCore/hooks/queries/availability/use_availability_requirements_validation";
import { AvailabilityMode, AvailabilityRule, AvailabilityRuleV2 } from "PFCore/types";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import css from "./edit_availability_side_panel.module.scss";

type EditAvailabilitySidePanelProps = {
  roleFormKey: RoleFormKey;
  onClose: VoidFunction;
  onApply: (roleFormKey: RoleFormKey, availability: AvailabilityRule) => void | Promise<void>;
};

export const EditAvailabilitySidePanel = ({
  roleFormKey,
  onClose,
  onApply
}: EditAvailabilitySidePanelProps) => {
  const { t: tTranslation } = useTranslation("translation");
  const { t } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });

  const roleTemplate = useTemplate(TemplateKey.Role);
  const {
    data: { availability: accountAvailability }
  } = useMyAccountInfo();

  const { getSingleErrorByType } = useResponseErrors();

  const defaultAvailabilityThreshold = roleTemplate?.schema.properties?.availability?.default_threshold ?? 70;
  const defaultAvailabilityMode = roleTemplate?.schema.properties?.availability?.default_mode as
    | AvailabilityMode
    | undefined;
  const allowedModes = roleTemplate?.schema.properties?.availability?.allowed_modes || AVAILABILITY_MODES;
  const allowedDurationModes =
    roleTemplate?.schema.definitions?.availability_duration?.properties?.mode?.enum || DURATION_MODES;

  const roleId = useWatch({
    name: `${roleFormKey}.${FIELD_ID_KEY}`
  });

  const convertedAvailability = useWatch({
    name: `${roleFormKey}.${FIELD_CONVERTED_AVAILABILITY_KEY}`
  }) as AvailabilityRule | null | undefined;
  const metadataAvailability = useWatch({
    name: `${roleFormKey}.${FIELD_AVAILABILITY_KEY}`
  }) as AvailabilityRule | null | undefined;
  const currentAvailability =
    metadataAvailability?.mode === AvailabilityMode.PerWeek ? convertedAvailability : metadataAvailability;

  const [draftAvailability, setDraftAvailability] = useState<Partial<AvailabilityRule>>(
    currentAvailability || {}
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { mutate: validateAvailability, isPending: isValidating } = useAvailabilityRequirementsValidation({
    onSuccess: () => {
      onApply(roleFormKey, draftAvailability as AvailabilityRule);
      onClose();
    },
    onError: ({ response }) => {
      setErrors(getSingleErrorByType(response) || {});
    }
  });

  const handleAvailabilityChange = (availability: AvailabilityRule | null) => {
    setDraftAvailability(availability || {});
    setErrors({});
  };

  const handleApply = () => {
    setErrors({});
    validateAvailability(camelizeApiKeys(draftAvailability) as Partial<AvailabilityRuleV2>);
  };

  return (
    <SidePanel
      show
      onClose={onClose}
      title={t("editAvailabilitySidePanel.title")}
      size="medium"
      fullHeight
      footerRenderer={({ onSidePanelClose }) => (
        <Inline spread>
          <Button kind="secondary" text={tTranslation("cancel")} onClick={onSidePanelClose} />
          <Button kind="primary" text={tTranslation("apply")} onClick={handleApply} disabled={isValidating} />
        </Inline>
      )}
    >
      <div className={css.content}>
        <Stack gap="spacingLg">
          <AvailabilityInput
            availability={draftAvailability}
            accountAvailability={accountAvailability}
            allowedModes={allowedModes}
            allowedDurationModes={allowedDurationModes}
            defaultAvailabilityMode={defaultAvailabilityMode}
            defaultAvailabilityThreshold={defaultAvailabilityThreshold}
            onChange={handleAvailabilityChange}
            errors={errors}
            isValidating={isValidating}
            isDisabled={isValidating}
            isFilter={false}
            allowRestore={false}
            allowClear={false}
          />
          {!roleId && draftAvailability.mode !== AvailabilityMode.PerWeek && (
            <Alert kind="general" title={t("editAvailabilitySidePanel.nonWeeklyModeInfo")} multiLineTitle />
          )}
        </Stack>
      </div>
    </SidePanel>
  );
};
